import React, { useEffect, useState } from 'react';
import { Controller, type Control, type FieldErrors, type FieldPath } from 'react-hook-form';
import { Col, Row } from 'antd';
import { AppSelect, AppTag, AppTextField, AppTooltip } from '@/components/atoms';
import RA2NDatePicker from '@/components/organisms/RA2NDatePicker/RA2NDatePicker';
import type { CreateMassagerFormData } from '@/schemas/createMassagerSchema';
import type { ClinicLocation } from '@/types/admin-clinic-detail';
import { normalizeLocationId } from '@/utils/locationId';

type ClinicLocationIdFieldValue =
  | CreateMassagerFormData['clinic_locations_a2'][number]['clinic_location_id']
  | CreateMassagerFormData['clinic_locations_r2'][number]['clinic_location_id']
  | null
  | undefined;

interface MassagerClinicLocationFormProps {
  control: Control<CreateMassagerFormData>;
  errors: FieldErrors<CreateMassagerFormData>;
  clinicLocationOptions: ClinicLocation[];
  selectedLocation?: ClinicLocation;
  /** Nested field path, e.g. "clinic_locations_a2.0.clinic_location_id". When not set, uses "clinic_location_id". */
  fieldName?:
    | `clinic_locations_a2.${number}.clinic_location_id`
    | `clinic_locations_r2.${number}.clinic_location_id`;
  /** Error message for this field (e.g. from errors.clinic_locations?.[i]?.clinic_location_id?.message). */
  errorMessage?: string;
  /** When true (e.g. section-level error), keep this block expanded so user sees the 拠点 field. */
  forceExpanded?: boolean;
  disabled?: boolean;
  /** List of clinic_location_ids already selected in sibling sections (used to disable duplicates). */
  usedLocationIds?: number[];
  /** Notify parent when this section expands or collapses. */
  onExpandedChange?: (expanded: boolean) => void;
  /** When true, section starts expanded on first mount (used for create screen + newly added sections). */
  autoExpandOnMount?: boolean;
  /** No649: When clinic site_type is upsystem, 登録日 is required. */
  isUpSystemSite?: boolean;
}

export const MassagerClinicLocationForm: React.FC<MassagerClinicLocationFormProps> = ({
  control,
  errors,
  clinicLocationOptions,
  selectedLocation,
  fieldName = 'clinic_location_id',
  errorMessage,
  forceExpanded = false,
  disabled = false,
  usedLocationIds,
  onExpandedChange,
  autoExpandOnMount = false,
  isUpSystemSite = false,
}) => {
  const [expanded, setExpanded] = useState(autoExpandOnMount);

  const toggleExpanded = (next: boolean) => {
    setExpanded(next);
    onExpandedChange?.(next);
  };

  useEffect(() => {
    if (errorMessage || forceExpanded) {
      setExpanded(true);
      onExpandedChange?.(true);
    }
  }, [errorMessage, forceExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const headerLabel = selectedLocation
    ? `${selectedLocation.location_name ?? '拠点'}: ${[selectedLocation.address1, selectedLocation.address2, selectedLocation.address3].filter(Boolean).join('')}`
    : '拠点';

  return (
    <div className={`rounded border border-gray-200 bg-white ${disabled ? 'opacity-75' : ''}`}>
      <div
        className="flex items-center justify-between gap-2 px-4 py-3 transition-colors"
        aria-expanded={expanded}
        aria-disabled={disabled}
      >
        <AppTooltip title={headerLabel} showOnlyOnOverflow>
          <span className="font-medium flex-1 min-w-0 line-clamp-1">{headerLabel}</span>
        </AppTooltip>
        <button
          type="button"
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded border border-gray-300 text-lg leading-none disabled:opacity-50 disabled:cursor-not-allowed ${disabled ? 'cursor-not-allowed bg-gray-100 text-gray-400' : 'cursor-pointer bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
          onClick={() => !disabled && toggleExpanded(!expanded)}
          aria-label={expanded ? '閉じる' : '開く'}
          disabled={disabled}
        >
          {expanded ? '−' : '+'}
        </button>
      </div>
      {expanded && (
        <div className="border-t border-gray-200 px-4 pb-4 pt-3">
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <label className="mb-1 block font-medium">
                拠点選択{' '}
                <AppTag color="magenta" className="ml-1">
                  必須
                </AppTag>
              </label>
              <Controller
                name={fieldName as FieldPath<CreateMassagerFormData>}
                control={control}
                render={({ field }) => {
                  const currentValue = normalizeLocationId(field.value as ClinicLocationIdFieldValue);
                  const usedSet = new Set(
                    (usedLocationIds ?? []).map(id => normalizeLocationId(id)).filter(id => id > 0)
                  );

                  return (
                    <div className="w-full min-w-0">
                      <AppSelect
                        {...field}
                        // Default selection is 「選択してください」 (value=0) for new/cleared rows
                        value={currentValue}
                        onChange={v =>
                          field.onChange(normalizeLocationId(v as ClinicLocationIdFieldValue))
                        }
                        options={[
                          { value: 0, label: '選択してください' },
                          ...clinicLocationOptions.map(loc => ({
                            value: loc.id,
                            label: `${loc.location_name}: ${[loc.address1, loc.address2, loc.address3].filter(Boolean).join('')}`,
                            // Disable locations already chosen in other sections; allow current selection.
                            disabled: usedSet.has(loc.id) && loc.id !== currentValue,
                          })),
                        ]}
                        status={errorMessage ? 'error' : undefined}
                        className="w-full min-w-0 max-w-full"
                        labelRender={({ label }) => (
                          <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                            {label}
                          </span>
                        )}
                        disabled={disabled}
                        // Show up to 5 options before scroll
                        listHeight={200}
                      />
                    </div>
                  );
                }}
              />
              {errorMessage && <p className="mt-1 text-sm text-red-500">{errorMessage}</p>}
            </Col>

            <Col span={24}>
              <label className="mb-1 block font-medium">
                適用期間 <AppTag color="magenta" className="ml-1">必須</AppTag>
              </label>
              <div className="flex items-stretch gap-2 rounded-md border border-gray-200 bg-white">
                <Controller
                  name={
                    (fieldName.replace(/\.clinic_location_id$/, '.available_from') as FieldPath<
                      CreateMassagerFormData
                    >)
                  }
                  control={control}
                  render={({ field }) => {
                    const match = fieldName.match(/\.(\d+)\.clinic_location_id$/);
                    const idx = match ? parseInt(match[1], 10) : 0;
                    const arrKey = fieldName.includes('_a2') ? 'clinic_locations_a2' : 'clinic_locations_r2';
                    const arrErr = (errors as Record<string, Record<number, { available_from?: { message?: string }; available_to?: { message?: string } }>>)?.[arrKey];
                    const errFrom = arrErr?.[idx];
                    const hasFromError = !!errFrom?.available_from?.message;
                    const strValue = typeof field.value === 'string' ? field.value : (field.value == null ? '' : String(field.value));
                    return (
                      <div className="min-w-0 flex-1">
                        <RA2NDatePicker
                          value={strValue}
                          onChange={v => field.onChange(v || null)}
                          placeholder="日付を入力"
                          disabled={!selectedLocation}
                          className="w-full"
                          error={hasFromError}
                        />
                      </div>
                    );
                  }}
                />
                <span className="flex shrink-0 items-center border-x border-gray-200 bg-gray-50 px-3 py-2 text-gray-600">
                  から
                </span>
                <Controller
                  name={
                    (fieldName.replace(/\.clinic_location_id$/, '.available_to') as FieldPath<
                      CreateMassagerFormData
                    >)
                  }
                  control={control}
                  render={({ field }) => {
                    const match = fieldName.match(/\.(\d+)\.clinic_location_id$/);
                    const idx = match ? parseInt(match[1], 10) : 0;
                    const arrKey = fieldName.includes('_a2') ? 'clinic_locations_a2' : 'clinic_locations_r2';
                    const arrErr = (errors as Record<string, Record<number, { available_from?: { message?: string }; available_to?: { message?: string } }>>)?.[arrKey];
                    const errTo = arrErr?.[idx];
                    const hasToError = !!errTo?.available_to?.message;
                    const strValue = typeof field.value === 'string' ? field.value : (field.value == null ? '' : String(field.value));
                    return (
                      <div className="min-w-0 flex-1">
                        <RA2NDatePicker
                          value={strValue}
                          onChange={v => field.onChange(v || null)}
                          placeholder="日付を入力"
                          disabled={!selectedLocation}
                          className="w-full"
                          error={hasToError}
                        />
                      </div>
                    );
                  }}
                />
              </div>
              {(() => {
                const match = fieldName.match(/\.(\d+)\.clinic_location_id$/);
                const idx = match ? parseInt(match[1], 10) : 0;
                const arrKey = fieldName.includes('_a2') ? 'clinic_locations_a2' : 'clinic_locations_r2';
                const arrErr = (errors as Record<string, Record<number, { available_from?: { message?: string }; available_to?: { message?: string } }>>)?.[arrKey];
                const rowErr = arrErr?.[idx];
                const msgFrom = rowErr?.available_from?.message;
                const msgTo = rowErr?.available_to?.message;
                const msg = msgFrom ?? msgTo;
                return msg ? <p className="mt-1 text-sm text-red-500">{msg}</p> : null;
              })()}
            </Col>

            {/* 登録日 & 廃止依頼日 — per 拠点, below 適用期間. Same width as available_from/available_to. */}
            <Col span={24}>
              <div className="flex items-stretch gap-2">
                <div className="min-w-0 flex-1">
                  <label className="mb-1 block font-medium">
                    登録日
                    {isUpSystemSite && (
                      <AppTag color="magenta" className="ml-1">
                        必須
                      </AppTag>
                    )}
                  </label>
                  <Controller
                    name={
                      (fieldName.replace(/\.clinic_location_id$/, '.regist_date') as FieldPath<
                        CreateMassagerFormData
                      >)
                    }
                    control={control}
                    render={({ field }) => {
                      const match = fieldName.match(/\.(\d+)\.clinic_location_id$/);
                      const idx = match ? parseInt(match[1], 10) : 0;
                      const arrKey = fieldName.includes('_a2') ? 'clinic_locations_a2' : 'clinic_locations_r2';
                      const arrErr = (errors as Record<string, Record<number, { regist_date?: { message?: string } }>>)?.[arrKey];
                      const errRegist = arrErr?.[idx];
                      const hasRegistError = !!errRegist?.regist_date?.message;
                      const strValue =
                        typeof field.value === 'string'
                          ? field.value
                          : field.value == null
                            ? ''
                            : String(field.value);
                      return (
                        <RA2NDatePicker
                          value={strValue}
                          onChange={v => field.onChange(v || null)}
                          className="w-full"
                          placeholder="日付を入力"
                          disabled={!selectedLocation}
                          error={hasRegistError}
                        />
                      );
                    }}
                  />
                  {(() => {
                    const match = fieldName.match(/\.(\d+)\.clinic_location_id$/);
                    const idx = match ? parseInt(match[1], 10) : 0;
                    const arrKey = fieldName.includes('_a2') ? 'clinic_locations_a2' : 'clinic_locations_r2';
                    const arrErr = (errors as Record<string, Record<number, { regist_date?: { message?: string } }>>)?.[arrKey];
                    const msg = arrErr?.[idx]?.regist_date?.message;
                    return msg ? <p className="mt-1 text-sm text-red-500">{msg}</p> : null;
                  })()}
                </div>
                {/* Invisible spacer: same width as から above, so 登録日/廃止依頼日 match available_from/available_to */}
                <span className="flex shrink-0 items-center px-3 py-2 text-transparent" aria-hidden>
                  から
                </span>
                <div className="min-w-0 flex-1">
                  <label className="mb-1 block font-medium">廃止依頼日</label>
                  <Controller
                    name={
                      (fieldName.replace(/\.clinic_location_id$/, '.stop_request_date') as FieldPath<
                        CreateMassagerFormData
                      >)
                    }
                    control={control}
                    render={({ field }) => {
                      const strValue =
                        typeof field.value === 'string'
                          ? field.value
                          : field.value == null
                            ? ''
                            : String(field.value);
                      return (
                        <RA2NDatePicker
                          value={strValue}
                          onChange={v => field.onChange(v || null)}
                          className="w-full"
                          placeholder="日付を入力"
                          disabled={!selectedLocation}
                        />
                      );
                    }}
                  />
                </div>
              </div>
            </Col>

            {/* RA2: 郵便番号 [3桁]-[4桁]、左右同幅でハイフン中央 */}
            <Col span={16}>
              <label className="mb-1 block font-medium">郵便番号</label>
              <div className="flex items-stretch overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                <span className="flex min-w-0 flex-1 items-center justify-start border-r border-gray-200 px-3 py-2 text-gray-700 tabular-nums">
                  {selectedLocation?.postal_code1 ?? ''}
                </span>
                <span className="flex shrink-0 items-center justify-center border-x border-gray-200 bg-gray-100 px-2 py-2 text-gray-500">
                  -
                </span>
                <span className="flex min-w-0 flex-1 items-center justify-start py-2 pl-2 pr-3 text-gray-700 tabular-nums">
                  {selectedLocation?.postal_code2 ?? ''}
                </span>
              </div>
            </Col>
            <Col span={8} />
            {/* RA2: 住所 = 1 label + one row with 3 inputs (address1, address2, address3) inside one continuous frame, like form-group row + 3 col */}
            <Col span={24}>
              <label className="mb-1 block font-medium">住所</label>
              <div className="flex overflow-hidden rounded-md border border-gray-200 bg-gray-50 [&_.ant-input]:border-0 [&_.ant-input]:rounded-none [&_.ant-input]:bg-transparent [&_.ant-input]:shadow-none [&_.ant-input]:outline-none">
                <div className="min-w-0 flex-1 border-r border-gray-200 last:border-r-0">
                  <AppTextField
                    readOnly
                    value={selectedLocation?.address1 ?? ''}
                    className="w-full border-0 bg-transparent py-2"
                  />
                </div>
                <div className="min-w-0 flex-1 border-r border-gray-200 last:border-r-0">
                  <AppTextField
                    readOnly
                    value={selectedLocation?.address2 ?? ''}
                    className="w-full border-0 bg-transparent py-2"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <AppTextField
                    readOnly
                    value={selectedLocation?.address3 ?? ''}
                    placeholder="建物名・部屋番号"
                    className="w-full border-0 bg-transparent py-2"
                  />
                </div>
              </div>
            </Col>
            <Col span={12}>
              <label className="mb-1 block font-medium">レセプト表示住所</label>
              <p className="min-h-[2.5rem] w-full min-w-0 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700 flex items-center">
                {selectedLocation?.rezept_address ?? ''}
              </p>
            </Col>
            <Col span={12}>
              <label className="mb-1 block font-medium">住所拠点名</label>
              <p className="min-h-[2.5rem] w-full min-w-0 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700 flex items-center">
                {selectedLocation?.base_name ?? ''}
              </p>
            </Col>
            <Col span={12}>
              <label className="mb-1 block font-medium">電話番号</label>
              <p className="min-h-[2.5rem] w-full min-w-0 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700 flex items-center">
                {selectedLocation?.telephone ?? ''}
              </p>
            </Col>
            <Col span={12}>
              <label className="mb-1 block font-medium">FAX</label>
              <p className="min-h-[2.5rem] w-full min-w-0 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700 flex items-center">
                {selectedLocation?.fax ?? ''}
              </p>
            </Col>
            <Col span={12}>
              <label className="mb-1 block font-medium">保健所登録区分</label>
              <p className="min-h-[2.5rem] w-full min-w-0 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700 flex items-center">
                {selectedLocation?.health_center_regist_type?.name ??
                  selectedLocation?.health_center_regist_type_id ??
                  ''}
              </p>
              {/* RA2: 選択中の拠点に保健所登録区分が設定されていない場合に警告 */}
              {selectedLocation &&
                (selectedLocation.health_center_regist_type_id == null ||
                  selectedLocation.health_center_regist_type_id === 0) && (
                  <p className="mt-1 text-sm text-amber-600">
                    選択中の拠点に保健所登録区分が設定されていません。
                  </p>
                )}
            </Col>
            <Col span={12} />
          </Row>
        </div>
      )}
    </div>
  );
};
